import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { endOfDay, format } from 'date-fns';
import { PiChartLineUpDuotone } from 'react-icons/pi';
import { Tooltip as MuiTooltip } from '@material-ui/core';

import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip } from 'recharts';

import { formatLabelValue } from 'app/admin/common/util/CommonUtil';
import defaultLabelStyle from 'app/admin/common/component/DefaultLabelStyle';

import OptimizationPipelinePopup, { OptimizationPipelinePopupHeader, PopupDateRangeQuickSelector, PopupToolbar, PopupToolbarGroup, PopupBody } from 'app/admin/optimizationPipelineV2/component/pipeline/popups/base/OptimizationPipelinePopup';

import OptimizationPipelinePopupTable from 'app/admin/optimizationPipelineV2/component/pipeline/popups/base/OptimizationPipelinePopupTable';

import { useOptimizationPipeline } from 'app/admin/optimizationPipelineV2/component/pipeline/providers/OptimizationPipelineProvider';

import { OptimizationPipelineChartFrame, OptimizationPipelineChartModeToggle, OptimizationPipelineChartTypeToggle, OptimizationPipelineChartViewToggle, OptimizationPipelineChartTooltip, OptimizationPipelineChartKeySelector, OptimizationPipelineChartEmptyState, toMs, normalizeNumber, colorForIndex, buildDateBounds, buildDisabledQuickOptions, formatPercent, pickUom, getChartPortalTooltipProps } from 'app/admin/optimizationPipelineV2/component/pipeline/popups/base/OptimizationPipelineChart';

const POPUP_TYPE = 'MODEL_VALIDATION_DETAILS_CHART';
const INITIAL_DATE_RANGE = { from: '', to: '', quickRange: 'ALL' };
const CHART_HEIGHT = 440;
const PCT_TOGGLE_BASE_CLASS = 'flex h-24 min-w-32 cursor-pointer items-center justify-center text-10 leading-none transition-colors px-8';

function getRowFeatures(row) { return row?.model_validation_data?.features || row?.features || null; }
function formatInputDate(date) { if (!(date instanceof Date) || Number.isNaN(date.getTime())) return ''; return format(date, 'yyyy-MM-dd'); }
function parseInputDate(value) { if (!value) return null; const date = new Date(value + 'T00:00:00'); if (Number.isNaN(date.getTime())) return null; return date; }
function formatDisplayDate(value) { const date = value instanceof Date ? value : parseInputDate(value); return date ? format(date, 'yyyy.MM.dd.') : ''; }

function getDateRangeSubtitle(dateRange) {
    const from = formatDisplayDate(dateRange?.from);
    const to = formatDisplayDate(dateRange?.to);
    if (from && to) return 'Showing data from ' + from + ' to ' + to;
    if (from) return 'Showing data from ' + from;
    if (to) return 'Showing data until ' + to;
    return 'Detailed model validation parameter history';
}

function getSeriesDisplayKey(key, valueType) { return key + '__' + valueType; }
function shouldUsePercentageDisplay(metric, percentageDisplayKeys, key, valueType) { if (metric === 'ape') return false; return !!percentageDisplayKeys[getSeriesDisplayKey(key, valueType)]; }
function getDisplayValue(rawValue, metric, percentageDisplayKeys, key, valueType) { if (rawValue == null || Number.isNaN(rawValue)) return null; if (shouldUsePercentageDisplay(metric, percentageDisplayKeys, key, valueType)) return rawValue * 100; return rawValue; }

function getPctToggleClass(enabled, extraClassName = '') {
    return PCT_TOGGLE_BASE_CLASS + ' ' + (enabled ? 'bg-grey-700 text-grey-100 hover:bg-grey-600' : 'bg-grey-300 text-grey-800 hover:bg-grey-400') + (extraClassName ? ' ' + extraClassName : '');
}

function collectAvailableKeysFromLogs(rows) {
    const set = new Set();

    (Array.isArray(rows) ? rows : []).forEach((row) => {
        const features = getRowFeatures(row);
        Object.keys(features || {}).forEach((key) => { if (key) set.add(key); });
    });

    return Array.from(set).sort((a, b) => a.localeCompare(b, 'hu'));
}

function renderTableValue(rawValue, currentMetric, pctKeys, sourceKey, valueType) {
    const displayValue = getDisplayValue(rawValue, currentMetric, pctKeys, sourceKey, valueType);
    if (currentMetric === 'ape') return formatPercent(displayValue);
    if (shouldUsePercentageDisplay(currentMetric, pctKeys, sourceKey, valueType)) return formatPercent(displayValue);
    return displayValue == null || Number.isNaN(displayValue) ? '-' : formatLabelValue(displayValue, defaultLabelStyle());
}

export default function ModelValidationDetailsChartPopup() {
    const { popup, closePopup, getOnDemandResource, ensureOnDemandResource } = useOptimizationPipeline();

    const chartRef = useRef(null);

    const [isChartView, setIsChartView] = useState(true);
    const [chartType, setChartType] = useState('line');
    const [metric, setMetric] = useState('value');
    const [dateRange, setDateRange] = useState(INITIAL_DATE_RANGE);
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [percentageDisplayKeys, setPercentageDisplayKeys] = useState({});
    const [hoveredKey, setHoveredKey] = useState(null);

    const open = popup?.type === POPUP_TYPE;
    const resource = getOnDemandResource('modelValidationLogDataList');
    const logDataList = resource?.data;
    const loading = resource?.loading;
    const error = resource?.error;

    const title = popup?.title || 'Model Validation Details';
    const uomMap = popup?.uomMap || null;
    const defaultSelectedKeys = popup?.defaultSelectedKeys || popup?.selectedKeys || [];

    useEffect(() => { if (open) ensureOnDemandResource('modelValidationLogDataList'); }, [open, ensureOnDemandResource]);

    const sortedRows = useMemo(() => {
        const list = Array.isArray(logDataList) ? logDataList : [];
        return [...list].filter((row) => row && row.ts).sort((a, b) => toMs(a.ts) - toMs(b.ts));
    }, [logDataList]);

    const allKeys = useMemo(() => {
        const popupKeys = Array.isArray(popup?.availableKeys) ? popup.availableKeys.filter(Boolean) : [];
        const logKeys = collectAvailableKeysFromLogs(sortedRows);
        return Array.from(new Set([...popupKeys, ...logKeys])).filter(Boolean).sort((a, b) => a.localeCompare(b, 'hu'));
    }, [popup?.availableKeys, sortedRows]);

    const dateBounds = useMemo(() => buildDateBounds(sortedRows), [sortedRows]);
    const disabledQuickOptions = useMemo(() => buildDisabledQuickOptions(dateBounds.minDate, dateBounds.maxDate), [dateBounds]);

    useEffect(() => {
        if (!open) return;

        setIsChartView(true);
        setChartType('line');
        setMetric('value');
        setHoveredKey(null);

        if (dateBounds.minDate && dateBounds.maxDate) setDateRange({ from: formatInputDate(dateBounds.minDate), to: formatInputDate(dateBounds.maxDate), quickRange: 'ALL' });
        else setDateRange(INITIAL_DATE_RANGE);

        const seed = Array.isArray(defaultSelectedKeys) && defaultSelectedKeys.length ? defaultSelectedKeys.filter(Boolean) : allKeys;

        setSelectedKeys(Array.from(new Set(seed)));
        setPercentageDisplayKeys({});
    }, [open, popup?.requestToken, dateBounds.minDate, dateBounds.maxDate, allKeys]);

    useEffect(() => {
        setPercentageDisplayKeys((prev) => {
            const next = {};
            selectedKeys.forEach((key) => {
                if (prev[key + '__true']) next[key + '__true'] = true;
                if (prev[key + '__est']) next[key + '__est'] = true;
                if (prev[key + '__ae']) next[key + '__ae'] = true;
            });
            return next;
        });
    }, [selectedKeys]);

    const togglePercentageDisplay = useCallback((key, type) => {
        const k = getSeriesDisplayKey(key, type);
        setPercentageDisplayKeys((prev) => ({ ...prev, [k]: !prev[k] }));
    }, []);

    const filteredData = useMemo(() => {
        if (!selectedKeys.length) return [];

        const fromDate = parseInputDate(dateRange?.from);
        const toDate = parseInputDate(dateRange?.to);
        const fromTime = fromDate ? fromDate.getTime() : -Infinity;
        const toTime = toDate ? endOfDay(toDate).getTime() : Infinity;
        const merged = {};

        for (const row of sortedRows) {
            const time = toMs(row.ts);
            if (!Number.isFinite(time) || time < fromTime || time > toTime) continue;

            const ts = row.ts;
            const features = getRowFeatures(row);

            if (!merged[ts]) merged[ts] = { ts };

            for (const key of selectedKeys) {
                const feature = features?.[key];

                merged[ts][key + '__true'] = normalizeNumber(feature?.true_value);
                merged[ts][key + '__est'] = normalizeNumber(feature?.estimated_value);
                merged[ts][key + '__ae'] = normalizeNumber(feature?.ae);

                const ape = normalizeNumber(feature?.ape);
                merged[ts][key + '__ape'] = ape == null ? null : ape * 100;
            }
        }

        return Object.values(merged).sort((a, b) => toMs(a.ts) - toMs(b.ts));
    }, [sortedRows, selectedKeys, dateRange]);

    const chartData = useMemo(() => (
        filteredData.map((row) => {
            const next = { ...row };

            selectedKeys.forEach((key) => {
                next[key + '__true_display'] = getDisplayValue(row[key + '__true'], metric, percentageDisplayKeys, key, 'true');
                next[key + '__est_display'] = getDisplayValue(row[key + '__est'], metric, percentageDisplayKeys, key, 'est');
                next[key + '__ae_display'] = getDisplayValue(row[key + '__ae'], metric, percentageDisplayKeys, key, 'ae');
                next[key + '__ape_display'] = row[key + '__ape'];
            });

            return next;
        })
    ), [filteredData, selectedKeys, metric, percentageDisplayKeys]);

    const isSeriesHighlighted = useCallback((key) => {
        if (!hoveredKey) return true;
        return hoveredKey === key;
    }, [hoveredKey]);

    const getSeriesOpacity = useCallback((key, highlightedOpacity = 1, mutedOpacity = 0.18) => (
        isSeriesHighlighted(key) ? highlightedOpacity : mutedOpacity
    ), [isSeriesHighlighted]);

    const getSeriesStrokeWidth = useCallback((key) => {
        if (!hoveredKey) return 2;
        return isSeriesHighlighted(key) ? 2 : 1;
    }, [hoveredKey, isSeriesHighlighted]);

    const getTooltip = useCallback(({ active, payload }) => {
        if (!(active && payload && payload.length)) return null;

        const row = payload[0] && payload[0].payload ? payload[0].payload : null;
        if (!row) return null;

        const date = new Date(row.ts);
        const tsLabel = Number.isNaN(date.getTime()) ? String(row.ts).slice(0, 10) : format(date, 'yyyy.MM.dd.');
        const fmtNum = (value) => (value == null || Number.isNaN(value)) ? '-' : formatLabelValue(value, defaultLabelStyle());
        const fmtPct = (value) => (value == null || Number.isNaN(value)) ? '-' : formatPercent(value);

        const renderRawAndPct = (raw, key, valueType, uom) => {
            if (!shouldUsePercentageDisplay(metric, percentageDisplayKeys, key, valueType)) return fmtNum(raw) + (uom ? ' ' + uom : '');
            const pctValue = raw == null || Number.isNaN(raw) ? null : raw * 100;
            return <span><span>{fmtPct(pctValue)}</span><span className='opacity-70'> (raw: {fmtNum(raw)}{uom ? ' ' + uom : ''})</span></span>;
        };

        return (
            <OptimizationPipelineChartTooltip title={tsLabel} minWidth={240} maxWidth={420}>
                {selectedKeys.map((key) => {
                    if (metric === 'value') {
                        const uom = pickUom(uomMap, key);
                        return <div key={key} className='font-medium'>{key}: <span className='opacity-80'>true</span> {renderRawAndPct(row[key + '__true'], key, 'true', uom)}, <span className='opacity-80'>est</span> {renderRawAndPct(row[key + '__est'], key, 'est', uom)}</div>;
                    }

                    if (metric === 'ae') {
                        const uom = pickUom(uomMap, key);
                        return <div key={key} className='font-medium'>{key}: {renderRawAndPct(row[key + '__ae'], key, 'ae', uom)}</div>;
                    }

                    return <div key={key} className='font-medium'>{key}: {fmtPct(row[key + '__ape'])}</div>;
                })}
            </OptimizationPipelineChartTooltip>
        );
    }, [selectedKeys, metric, uomMap, percentageDisplayKeys]);

    const tableConfig = useMemo(() => {
        const columns = [
            { key: 'ts', label: 'Timestamp', sortable: true, width: 140, minWidth: 140, align: 'left', getSortValue: (row) => row?.ts, renderCell: ({ row }) => format(new Date(row.ts), 'yyyy.MM.dd.') }
        ];

        if (metric === 'value') {
            selectedKeys.forEach((key) => {
                columns.push({ key: key + '__true', label: key + ' true', sortable: true, minWidth: 160, align: 'right', renderCell: ({ row }) => renderTableValue(row[key + '__true'], metric, percentageDisplayKeys, key, 'true') });
                columns.push({ key: key + '__est', label: key + ' est', sortable: true, minWidth: 160, align: 'right', renderCell: ({ row }) => renderTableValue(row[key + '__est'], metric, percentageDisplayKeys, key, 'est') });
            });
        } else {
            selectedKeys.forEach((key) => {
                const valueType = metric === 'ae' ? 'ae' : 'ape';
                columns.push({ key: key + '__' + valueType, label: key, sortable: true, minWidth: 160, align: 'right', renderCell: ({ row }) => renderTableValue(row[key + '__' + valueType], metric, percentageDisplayKeys, key, valueType) });
            });
        }

        return { columns: columns, sort: { key: 'ts', direction: 'desc' }, states: { loadingTitle: 'Loading model validation details...', errorTitle: 'Failed to load model validation details', emptyTitle: 'No data available', noResultsTitle: 'No matching rows' }, tableMinWidth: columns.length > 6 ? Math.max(900, columns.length * 160) : null };
    }, [selectedKeys, metric, percentageDisplayKeys]);

    function renderChipActions({ key }) {
        if (metric !== 'value') return null;

        return (
            <div className='flex -ml-12 overflow-hidden rounded-r-full' onMouseDown={(event) => { event.preventDefault(); event.stopPropagation(); }} onClick={(event) => { event.preventDefault(); event.stopPropagation(); }}>
                <MuiTooltip title='Display true as percentage' arrow><span onClick={(event) => { event.preventDefault(); event.stopPropagation(); togglePercentageDisplay(key, 'true'); }} className={getPctToggleClass(!!percentageDisplayKeys[key + '__true'], 'pl-16 pr-8')}>T%</span></MuiTooltip>
                <MuiTooltip title='Display estimated as percentage' arrow><span onClick={(event) => { event.preventDefault(); event.stopPropagation(); togglePercentageDisplay(key, 'est'); }} className={getPctToggleClass(!!percentageDisplayKeys[key + '__est'], 'pr-10 rounded-r-full border-l border-black/10')}>E%</span></MuiTooltip>
            </div>
        );
    }

    const hasKeys = allKeys.length > 0;
    const hasFilteredData = filteredData.length > 0;
    const tooltipProps = getChartPortalTooltipProps(getTooltip, chartRef);

    return (
        <OptimizationPipelinePopup open={open} onClose={closePopup} width='1440px' minWidth={480} maxWidth={1440} overflowVisible>
            <OptimizationPipelinePopupHeader title={title} subtitle={getDateRangeSubtitle(dateRange)} icon={PiChartLineUpDuotone} onClose={closePopup}>
                <PopupDateRangeQuickSelector value={dateRange} onChange={setDateRange} minDate={dateBounds.minDate} maxDate={dateBounds.maxDate} disabledQuickOptions={disabledQuickOptions} />
            </OptimizationPipelinePopupHeader>

            {hasKeys && (
                <PopupToolbar className='flex-col items-stretch'>
                    <OptimizationPipelineChartKeySelector options={allKeys} value={selectedKeys} onChange={setSelectedKeys} getColor={(key, index, selected) => colorForIndex(index, selected.length)} renderChipActions={renderChipActions} onHoverKeyChange={setHoveredKey} />

                    <div className='w-full flex items-center justify-between gap-16'>
                        <PopupToolbarGroup label='Metric:'>
                            <OptimizationPipelineChartModeToggle value={metric} onChange={setMetric} options={[{ value: 'value', label: 'True/Est' }, { value: 'ae', label: 'AE' }, { value: 'ape', label: 'APE' }]} />
                        </PopupToolbarGroup>

                        <PopupToolbarGroup>
                            <OptimizationPipelineChartTypeToggle value={chartType} onChange={setChartType} disabled={!isChartView} />
                            <OptimizationPipelineChartViewToggle isChartView={isChartView} onChange={setIsChartView} />
                        </PopupToolbarGroup>
                    </div>
                </PopupToolbar>
            )}

            <PopupBody padding={false} scroll>
                <div className={isChartView ? 'p-20' : 'p-0'}>
                    {loading && <OptimizationPipelineChartEmptyState>Loading model validation details...</OptimizationPipelineChartEmptyState>}
                    {!loading && error && <OptimizationPipelineChartEmptyState>Failed to load model validation details.</OptimizationPipelineChartEmptyState>}
                    {!loading && !error && !hasKeys && <OptimizationPipelineChartEmptyState>No keys available.</OptimizationPipelineChartEmptyState>}
                    {!loading && !error && hasKeys && selectedKeys.length === 0 && <OptimizationPipelineChartEmptyState>Select at least one feature.</OptimizationPipelineChartEmptyState>}
                    {!loading && !error && hasKeys && selectedKeys.length > 0 && !hasFilteredData && <OptimizationPipelineChartEmptyState>No data available.</OptimizationPipelineChartEmptyState>}

                    {!loading && !error && hasFilteredData && (
                        isChartView ? (
                            <div className='w-full' style={{ height: CHART_HEIGHT, minHeight: CHART_HEIGHT }}>
                                <OptimizationPipelineChartFrame ref={chartRef} height={CHART_HEIGHT} minHeight={CHART_HEIGHT} overflowVisible>
                                    {chartType === 'bar' ? (
                                        <BarChart data={chartData} margin={{ top: 10, left: -15, right: 10, bottom: 10 }}>
                                            <CartesianGrid strokeDasharray='3 3' />
                                            <XAxis dataKey='ts' tickMargin={10} tickFormatter={(tick) => format(new Date(tick), 'yyyy.MM.dd.')} tickCount={10} />
                                            <YAxis orientation='left' width={80} tickMargin={10} tickCount={10} type='number' tickFormatter={(tick) => formatLabelValue(tick, { thousandsSeparator: false, decimalPlaces: 2 })} domain={['dataMin', 'dataMax']} />
                                            <ReTooltip {...tooltipProps} />

                                            {metric === 'value' && selectedKeys.map((key, index) => {
                                                const color = colorForIndex(index, selectedKeys.length);
                                                return <React.Fragment key={key}><Bar dataKey={key + '__true_display'} name={key + ' true'} fill={color} fillOpacity={getSeriesOpacity(key)} isAnimationActive={false} /><Bar dataKey={key + '__est_display'} name={key + ' est'} fill={color} fillOpacity={getSeriesOpacity(key, 0.35, 0.1)} isAnimationActive={false} /></React.Fragment>;
                                            })}

                                            {metric === 'ae' && selectedKeys.map((key, index) => <Bar key={key} dataKey={key + '__ae_display'} name={key} fill={colorForIndex(index, selectedKeys.length)} fillOpacity={getSeriesOpacity(key)} isAnimationActive={false} />)}
                                            {metric === 'ape' && selectedKeys.map((key, index) => <Bar key={key} dataKey={key + '__ape_display'} name={key} fill={colorForIndex(index, selectedKeys.length)} fillOpacity={getSeriesOpacity(key)} isAnimationActive={false} />)}
                                        </BarChart>
                                    ) : (
                                        <LineChart data={chartData} margin={{ top: 10, left: -15, right: 10, bottom: 10 }}>
                                            <CartesianGrid strokeDasharray='3 3' />
                                            <XAxis dataKey='ts' tickMargin={10} tickFormatter={(tick) => format(new Date(tick), 'yyyy.MM.dd.')} tickCount={10} />
                                            <YAxis orientation='left' width={80} tickMargin={10} tickCount={10} type='number' tickFormatter={(tick) => formatLabelValue(tick, { thousandsSeparator: false, decimalPlaces: 2 })} domain={['dataMin', 'dataMax']} />
                                            <ReTooltip {...tooltipProps} />

                                            {metric === 'value' && selectedKeys.map((key, index) => {
                                                const color = colorForIndex(index, selectedKeys.length);
                                                return <React.Fragment key={key}><Line type='monotone' dataKey={key + '__true_display'} name={key + ' true'} stroke={color} strokeWidth={getSeriesStrokeWidth(key)} strokeOpacity={getSeriesOpacity(key)} dot={false} activeDot={{ r: isSeriesHighlighted(key) ? 4 : 2 }} /><Line type='monotone' dataKey={key + '__est_display'} name={key + ' est'} stroke={color} strokeWidth={getSeriesStrokeWidth(key)} strokeOpacity={getSeriesOpacity(key)} dot={false} strokeDasharray='4 2' activeDot={{ r: isSeriesHighlighted(key) ? 4 : 2 }} /></React.Fragment>;
                                            })}

                                            {metric === 'ae' && selectedKeys.map((key, index) => <Line key={key} type='monotone' dataKey={key + '__ae_display'} name={key} stroke={colorForIndex(index, selectedKeys.length)} strokeWidth={getSeriesStrokeWidth(key)} strokeOpacity={getSeriesOpacity(key)} dot={false} activeDot={{ r: isSeriesHighlighted(key) ? 4 : 2 }} />)}
                                            {metric === 'ape' && selectedKeys.map((key, index) => <Line key={key} type='monotone' dataKey={key + '__ape_display'} name={key} stroke={colorForIndex(index, selectedKeys.length)} strokeWidth={getSeriesStrokeWidth(key)} strokeOpacity={getSeriesOpacity(key)} dot={false} activeDot={{ r: isSeriesHighlighted(key) ? 4 : 2 }} />)}
                                        </LineChart>
                                    )}
                                </OptimizationPipelineChartFrame>
                            </div>
                        ) : (
                            <OptimizationPipelinePopupTable tableConfig={tableConfig} rows={filteredData} minHeight={400} rowKey={(row, index) => row?.ts || index} />
                        )
                    )}
                </div>
            </PopupBody>
        </OptimizationPipelinePopup>
    );
}